import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Shield, Award, MessageSquareText, ArrowRight, CheckCircle, GraduationCap, BookOpen, FileText, Building, Users, MapPin, Clock, Home as HomeIcon, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Los 7 rangos oficiales del CNI
const RANGOS = [
  { value: 'Profesional Inmobiliario', label: 'Profesional Inmobiliario', short: 'Profesional' },
  { value: 'Profesional Inmobiliario Avanzado', label: 'Profesional Avanzado', short: 'Prof. Avanzado' },
  { value: 'Licenciado Inmobiliario', label: 'Licenciado Inmobiliario', short: 'Licenciado' },
  { value: 'Máster Inmobiliario', label: 'Máster Inmobiliario', short: 'Máster' },
  { value: 'Máster Inmobiliario Avanzado', label: 'Máster Avanzado', short: 'Máster Avanzado' },
  { value: 'Doctor Inmobiliario', label: 'Doctor Inmobiliario', short: 'Doctor' },
  { value: 'Doctor Honoris Causa', label: 'Doctor Honoris Causa', short: 'Honoris Causa' },
];

const TIPOS_OPERACION = [
  'Venta',
  'Renta',
  'Traspaso',
  'Preventa',
  'Inversión',
];

const ESPECIALIDADES = [
  'Residencial',
  'Comercial',
  'Industrial',
  'Terrenos',
  'Desarrollos',
  'Lujo',
];

const TIPOS_PROPIEDAD = [
  'Casa',
  'Departamento',
  'Oficina',
  'Local Comercial',
  'Bodega',
  'Terreno',
  'Edificio',
];

const EXPERIENCIA = [
  { value: '1-3', label: '1-3 años' },
  { value: '3-5', label: '3-5 años' },
  { value: '5-10', label: '5-10 años' },
  { value: '10-20', label: '10-20 años' },
  { value: '20+', label: 'Más de 20 años' },
];

export const Home = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState(null);
  const [featuredProfessionals, setFeaturedProfessionals] = useState([]);
  const [featuredPrograms, setFeaturedPrograms] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ estados: [], ciudades: [] });
  
  // Filtros
  const [filters, setFilters] = useState({
    rango: '',
    tipo_operacion: '',
    especialidad: '',
    tipo_propiedad: '',
    experiencia: '',
    ubicacion: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, profsRes, progsRes, filtersRes] = await Promise.all([
          fetch(`${API}/stats`),
          fetch(`${API}/professionals?limit=6`),
          fetch(`${API}/academic-programs`),
          fetch(`${API}/filter-options`)
        ]);
        
        if (statsRes.ok) setStats(await statsRes.json());
        if (profsRes.ok) setFeaturedProfessionals(await profsRes.json());
        if (progsRes.ok) {
          const data = await progsRes.json();
          setFeaturedPrograms(data.programs?.filter(p => p.destacado).slice(0, 3) || []);
        }
        if (filtersRes.ok) setFilterOptions(await filtersRes.json());
      } catch (error) {
        console.error('Error:', error);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (filters.rango) params.set('rango', filters.rango);
    if (filters.tipo_operacion) params.set('tipo_operacion', filters.tipo_operacion);
    if (filters.especialidad) params.set('especialidad', filters.especialidad);
    if (filters.ubicacion) params.set('estado', filters.ubicacion);
    navigate(`/buscar?${params.toString()}`);
  };

  const handleRangoClick = (rango) => {
    navigate(`/buscar?rango=${encodeURIComponent(rango)}`);
  };

  return (
    <div className="page-transition" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative bg-[#0a0a0a] overflow-hidden min-h-[90vh] flex items-center">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-[#111111] to-transparent" />
        </div>
        
        <div className="container-cni relative z-10">
          <div className="max-w-4xl mx-auto text-center py-20">
            {/* Logo Grande */}
            <div className="flex justify-center mb-6">
              <img 
                src="/images/logo-cni.png" 
                alt="Colegio Nacional de Inmobiliarios" 
                className="w-full max-w-4xl px-4"
              />
            </div>

            <p className="text-lg md:text-xl text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
              Registro público verificable de profesionales inmobiliarios en México. 
              Garantizamos confianza y certeza en cada transacción.
            </p>

            {/* Search Form */}
            <form onSubmit={handleSearch} className="max-w-4xl mx-auto mb-6">
              {/* Barra de búsqueda principal */}
              <div className="flex gap-3 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                  <Input
                    type="text"
                    placeholder="Buscar por nombre o número de registro"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-14 pl-12 text-base bg-[#1a1a1a] border-[#2a2a2a] text-white placeholder-gray-500 rounded-lg focus:border-amber-500"
                    data-testid="hero-search-input"
                  />
                </div>
                <Button 
                  type="submit"
                  className="h-14 px-8 bg-amber-500 hover:bg-amber-400 text-black rounded-lg font-semibold"
                  data-testid="hero-search-btn"
                >
                  Buscar
                </Button>
              </div>

              {/* Filtros avanzados */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {/* Tipo de Operación */}
                <Select value={filters.tipo_operacion} onValueChange={(v) => setFilters({...filters, tipo_operacion: v})}>
                  <SelectTrigger className="h-11 bg-[#222222] border-[#444444] text-white font-medium">
                    <Briefcase className="w-4 h-4 mr-2 text-amber-500" />
                    <SelectValue placeholder="Operación" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#222222] border-[#444444]">
                    <SelectItem value="all" className="text-white font-medium">Todas</SelectItem>
                    {TIPOS_OPERACION.map(tipo => (
                      <SelectItem key={tipo} value={tipo} className="text-white font-medium">{tipo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Especialidad */}
                <Select value={filters.especialidad} onValueChange={(v) => setFilters({...filters, especialidad: v})}>
                  <SelectTrigger className="h-11 bg-[#222222] border-[#444444] text-white font-medium">
                    <Award className="w-4 h-4 mr-2 text-amber-500" />
                    <SelectValue placeholder="Especialidad" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#222222] border-[#444444]">
                    <SelectItem value="all" className="text-white font-medium">Todas</SelectItem>
                    {ESPECIALIDADES.map(esp => (
                      <SelectItem key={esp} value={esp} className="text-white font-medium">{esp}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Tipo de Propiedad */}
                <Select value={filters.tipo_propiedad} onValueChange={(v) => setFilters({...filters, tipo_propiedad: v})}>
                  <SelectTrigger className="h-11 bg-[#222222] border-[#444444] text-white font-medium">
                    <HomeIcon className="w-4 h-4 mr-2 text-amber-500" />
                    <SelectValue placeholder="Propiedad" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#222222] border-[#444444]">
                    <SelectItem value="all" className="text-white font-medium">Todas</SelectItem>
                    {TIPOS_PROPIEDAD.map(tipo => (
                      <SelectItem key={tipo} value={tipo} className="text-white font-medium">{tipo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Experiencia */}
                <Select value={filters.experiencia} onValueChange={(v) => setFilters({...filters, experiencia: v})}>
                  <SelectTrigger className="h-11 bg-[#222222] border-[#444444] text-white font-medium">
                    <Clock className="w-4 h-4 mr-2 text-amber-500" />
                    <SelectValue placeholder="Experiencia" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#222222] border-[#444444]">
                    <SelectItem value="all" className="text-white font-medium">Cualquiera</SelectItem>
                    {EXPERIENCIA.map(exp => (
                      <SelectItem key={exp.value} value={exp.value} className="text-white font-medium">{exp.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Rango */}
                <Select value={filters.rango} onValueChange={(v) => setFilters({...filters, rango: v})}>
                  <SelectTrigger className="h-11 bg-[#222222] border-[#444444] text-white font-medium">
                    <Shield className="w-4 h-4 mr-2 text-amber-500" />
                    <SelectValue placeholder="Rango CNI" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#222222] border-[#444444]">
                    <SelectItem value="all" className="text-white font-medium">Todos</SelectItem>
                    {RANGOS.map(rango => (
                      <SelectItem key={rango.value} value={rango.value} className="text-white font-medium">{rango.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Ubicación */}
                <Select value={filters.ubicacion} onValueChange={(v) => setFilters({...filters, ubicacion: v})}>
                  <SelectTrigger className="h-11 bg-[#222222] border-[#444444] text-white font-medium">
                    <MapPin className="w-4 h-4 mr-2 text-amber-500" />
                    <SelectValue placeholder="Ubicación" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#222222] border-[#444444]">
                    <SelectItem value="all" className="text-white font-medium">Todo México</SelectItem>
                    {filterOptions.estados?.map(estado => (
                      <SelectItem key={estado} value={estado} className="text-white font-medium">{estado}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </form>

            {/* Botones de Rangos CNI */}
            <div className="mb-8">
              <p className="text-gray-400 text-sm mb-4 text-center">Buscar por rango del Colegio:</p>
              <div className="flex flex-wrap justify-center gap-3">
                {RANGOS.map((rango) => (
                  <button
                    key={rango.value}
                    onClick={() => handleRangoClick(rango.value)}
                    className="px-5 py-2.5 rounded-full bg-[#222222] border border-[#444444] text-white hover:bg-amber-500 hover:text-black hover:border-amber-500 transition-all text-sm font-semibold"
                  >
                    {rango.short}
                  </button>
                ))}
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
              <Link to="/registro">
                <Button className="h-12 px-8 bg-amber-500 hover:bg-amber-400 text-black font-bold text-base">
                  Unirme al Colegio
                </Button>
              </Link>
              <Link to="/buscar">
                <Button className="h-12 px-8 bg-[#333333] hover:bg-[#444444] text-white font-bold text-base border border-[#555555]">
                  Buscar Profesional
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {stats && (
        <section className="bg-[#111111] py-12 border-y border-[#1a1a1a]">
          <div className="container-cni">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl md:text-4xl font-manrope font-bold text-amber-400 mb-1">
                  {stats.active_professionals}+
                </div>
                <div className="text-gray-500 text-sm">Profesionales Activos</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-manrope font-bold text-amber-400 mb-1">
                  {stats.total_associations}
                </div>
                <div className="text-gray-500 text-sm">Asociaciones Afiliadas</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-manrope font-bold text-amber-400 mb-1">
                  {stats.total_programs}
                </div>
                <div className="text-gray-500 text-sm">Programas Académicos</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-manrope font-bold text-amber-400 mb-1">
                  7
                </div>
                <div className="text-gray-500 text-sm">Rangos Profesionales</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="section-spacing bg-[#0a0a0a]">
        <div className="container-cni">
          <div className="text-center mb-12">
            <h2 className="font-manrope font-bold text-2xl md:text-3xl text-white mb-4">
              ¿Por qué confiar en el CNI?
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Somos el registro público verificable de profesionales inmobiliarios más confiable de México
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-8 text-center hover:border-amber-500/30 transition-colors">
              <div className="w-16 h-16 bg-amber-500/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="font-manrope font-semibold text-lg text-white mb-3">
                Verificación Institucional
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Validación rigurosa de credenciales, documentación y certificaciones profesionales.
              </p>
            </div>
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-8 text-center hover:border-amber-500/30 transition-colors">
              <div className="w-16 h-16 bg-amber-500/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                <Award className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="font-manrope font-semibold text-lg text-white mb-3">
                Sistema de 7 Rangos
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Clasificación basada en formación académica, experiencia y trayectoria comprobable.
              </p>
            </div>
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-8 text-center hover:border-amber-500/30 transition-colors">
              <div className="w-16 h-16 bg-amber-500/10 rounded-xl flex items-center justify-center mx-auto mb-6">
                <GraduationCap className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="font-manrope font-semibold text-lg text-white mb-3">
                Formación Avalada
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Cursos, diplomados y certificaciones avaladas por el Colegio y CONOCER.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Academic Programs Section - Oferta Académica */}
      <section className="section-spacing bg-[#111111]">
        <div className="container-cni">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="font-manrope font-bold text-2xl text-white mb-2">
                Oferta Académica Validada
              </h2>
              <p className="text-gray-400">Programas avalados por el Colegio Nacional de Inmobiliarios</p>
            </div>
            <Link to="/oferta-academica">
              <Button className="hidden sm:flex bg-[#333333] hover:bg-[#444444] text-white font-bold border border-[#555555]">
                Ver todos
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Programa 1 */}
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 hover:border-amber-500/30 transition-colors">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 font-semibold">Diplomado</Badge>
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30 font-semibold">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Validado
                </Badge>
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">Diplomado en Bienes Raíces</h3>
              <p className="text-sm text-gray-400 mb-3">Universidad Inmobiliaria de México</p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-white font-medium">120 horas</span>
                <Link to="/oferta-academica" className="text-amber-400 font-bold hover:text-amber-300">Ver más →</Link>
              </div>
            </div>

            {/* Programa 2 */}
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 hover:border-amber-500/30 transition-colors">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 font-semibold">Certificación</Badge>
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30 font-semibold">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  CONOCER
                </Badge>
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">EC0110 Asesoría Inmobiliaria</h3>
              <p className="text-sm text-gray-400 mb-3">Estándar de Competencia CONOCER</p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-white font-medium">40 horas</span>
                <Link to="/oferta-academica" className="text-amber-400 font-bold hover:text-amber-300">Ver más →</Link>
              </div>
            </div>

            {/* Programa 3 */}
            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 hover:border-amber-500/30 transition-colors">
              <div className="flex items-center gap-2 mb-3">
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 font-semibold">Maestría</Badge>
                <Badge className="bg-green-500/20 text-green-400 border-green-500/30 font-semibold">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Validado
                </Badge>
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">Maestría en Valuación Inmobiliaria</h3>
              <p className="text-sm text-gray-400 mb-3">Instituto de Valuación Nacional</p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-white font-medium">2 años</span>
                <Link to="/oferta-academica" className="text-amber-400 font-bold hover:text-amber-300">Ver más →</Link>
              </div>
            </div>
          </div>

          {/* CTA Oferta Académica */}
          <div className="text-center mt-8">
            <Link to="/oferta-academica">
              <Button className="bg-amber-500 hover:bg-amber-400 text-black font-bold px-8">
                <GraduationCap className="w-5 h-5 mr-2" />
                Explorar toda la oferta académica
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Access */}
      <section className="section-spacing bg-[#0a0a0a]">
        <div className="container-cni">
          <div className="text-center mb-10">
            <h2 className="font-manrope font-bold text-2xl text-white mb-2">
              Información del Colegio
            </h2>
            <p className="text-gray-400">Conoce más sobre nuestra institución</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/rangos" className="group">
              <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 h-full hover:border-amber-500/30 transition-colors">
                <Award className="w-10 h-10 text-amber-400 mb-4" />
                <h3 className="font-manrope font-semibold text-white text-lg mb-2">Rangos del Colegio</h3>
                <p className="text-gray-400 text-sm mb-4">7 niveles de reconocimiento profesional</p>
                <span className="inline-flex items-center px-4 py-2 bg-[#333333] hover:bg-amber-500 text-white hover:text-black rounded-lg text-sm font-bold transition-all">
                  Ver rangos <ArrowRight className="w-4 h-4 ml-2" />
                </span>
              </div>
            </Link>
            <Link to="/membresia" className="group">
              <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 h-full hover:border-amber-500/30 transition-colors">
                <Users className="w-10 h-10 text-amber-400 mb-4" />
                <h3 className="font-manrope font-semibold text-white text-lg mb-2">Membresía Anual</h3>
                <p className="text-gray-400 text-sm mb-4">$1,500 MXN + IVA / Vigencia 12 meses</p>
                <span className="inline-flex items-center px-4 py-2 bg-[#333333] hover:bg-amber-500 text-white hover:text-black rounded-lg text-sm font-bold transition-all">
                  Ver beneficios <ArrowRight className="w-4 h-4 ml-2" />
                </span>
              </div>
            </Link>
            <Link to="/reglas-marca" className="group">
              <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 h-full hover:border-amber-500/30 transition-colors">
                <FileText className="w-10 h-10 text-amber-400 mb-4" />
                <h3 className="font-manrope font-semibold text-white text-lg mb-2">Reglas de la Marca</h3>
                <p className="text-gray-400 text-sm mb-4">Reglamento de uso de la marca colectiva</p>
                <span className="inline-flex items-center px-4 py-2 bg-[#333333] hover:bg-amber-500 text-white hover:text-black rounded-lg text-sm font-bold transition-all">
                  Ver reglamento <ArrowRight className="w-4 h-4 ml-2" />
                </span>
              </div>
            </Link>
            <Link to="/asociaciones" className="group">
              <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6 h-full hover:border-amber-500/30 transition-colors">
                <Building className="w-10 h-10 text-amber-400 mb-4" />
                <h3 className="font-manrope font-semibold text-white text-lg mb-2">Asociaciones</h3>
                <p className="text-gray-400 text-sm mb-4">Conoce las asociaciones afiliadas al CNI</p>
                <span className="inline-flex items-center px-4 py-2 bg-[#333333] hover:bg-amber-500 text-white hover:text-black rounded-lg text-sm font-bold transition-all">
                  Ver asociaciones <ArrowRight className="w-4 h-4 ml-2" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Professionals */}
      {featuredProfessionals.length > 0 && (
        <section className="section-spacing bg-[#111111]">
          <div className="container-cni">
            <div className="flex justify-between items-center mb-8">
              <div>
                <h2 className="font-manrope font-bold text-2xl text-white mb-2">
                  Profesionales Verificados
                </h2>
                <p className="text-gray-400">Conoce a nuestros profesionales certificados</p>
              </div>
              <Link to="/buscar">
                <Button className="hidden sm:flex bg-[#333333] hover:bg-[#444444] text-white font-bold border border-[#555555]">
                  Ver directorio
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredProfessionals.slice(0, 6).map((prof) => (
                <Link 
                  key={prof.id} 
                  to={`/profesional/${prof.id}`}
                  className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-5 hover:border-amber-500/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <img
                      src={prof.foto || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(prof.nombre)}&backgroundColor=1a1a1a`}
                      alt={prof.nombre}
                      className="w-14 h-14 rounded-xl object-cover bg-[#2a2a2a]"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-white truncate">{prof.nombre}</h3>
                      <p className="text-sm text-gray-500">{prof.ciudad}, {prof.estado}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="rank-badge text-xs">{prof.rango}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section-spacing bg-[#0a0a0a]">
        <div className="container-cni">
          <div className="bg-gradient-to-r from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-2xl p-8 md:p-12 text-center">
            <h2 className="font-manrope font-bold text-2xl md:text-3xl text-white mb-4">
              ¿Eres profesional inmobiliario?
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              Únete al Colegio Nacional de Inmobiliarios y forma parte del registro profesional más confiable de México
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/registro">
                <Button className="h-12 px-8 bg-amber-500 hover:bg-amber-400 text-black font-semibold">
                  Unirme al Colegio
                </Button>
              </Link>
              <Link to="/como-funciona">
                <Button variant="outline" className="h-12 px-8 border-amber-500/30 text-amber-400 hover:bg-amber-500/10">
                  Conocer requisitos
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
